// Customer relationship management (teacher portal /customers): data model.
//
// Pure (no Firebase or Node imports): the teacher portal imports this folder directly and computes everything
// (identity matching, lifecycle, lifetime value) in the browser. Only the manager's decisions are stored:
// customers/{id} (confirmed links, notes, overrides) and crmRejectedPairs/{id}. See docs/crm-design.md.
// Money is integer pence throughout. Dates are ISO strings (YYYY-MM-DD, or full ISO timestamps where noted).

import type { HolidayInvoice, HolidayRegistration, HolidayTerm } from "../holiday/holidayInvoicing";
import type { PreschoolInvoice } from "../preschool/preschoolInvoicing";

// ---------------------------------------------------------------------------------------------------------------
// Inputs: the Firestore documents the CRM reads (only the fields it uses).

/** children/{id} */
export interface CrmChildDoc {
  id: string;
  registrationId?: string;
  parentEmail?: string;
  ignore?: boolean;
}

/** registrations/{id}. Dates may be strings or Firestore Timestamps. */
export interface CrmRegistrationDoc {
  id: string;
  timeStamp?: unknown;
  email?: string;
  childFirstName?: string;
  childMiddleName?: string;
  childLastName?: string;
  childDateOfBirth?: unknown;
  parent1FullName?: string;
  parent1Address?: string;
  parent1Phone?: string;
  parent1Email?: string;
  parent2FullName?: string;
  parent2Address?: string;
  parent2Phone?: string;
  parent2Email?: string;
  /** "Monday - Morning, Monday - Afternoon, ..." */
  enrolment?: string;
  /** "15 hour funded" | "30 hour funded" | "Not yet entitled" */
  fundingEntitlement?: string;
  startDate?: unknown;
  ignore?: boolean;
}

export type CrmPreschoolInvoice = Pick<PreschoolInvoice,
  "ref" | "childId" | "childName" | "parentName" | "email" | "termId" | "kind" | "period" | "totalPence" | "paid" |
  "paidDate" | "cancelled" | "issuedDate">;

export type CrmHolidayInvoice = Pick<HolidayInvoice,
  "invoiceNo" | "termId" | "registrationIds" | "email" | "parentName" | "children" | "fullDayPricePence" |
  "halfDayPricePence" | "totalPence" | "paid" | "paidDate" | "cancelled" | "issuedDate">;

export type CrmHolidayRegistration = Pick<HolidayRegistration, "id" | "termId" | "form" | "admin" | "invoiceNo" | "removedFromForm">;

export type CrmHolidayTerm = Pick<HolidayTerm, "id" | "name" | "formCreatedAt" | "days">;

// ---------------------------------------------------------------------------------------------------------------
// Stored manager decisions.

export type Stage = "enquiry" | "registered" | "active" | "holidayOnly" | "lapsed" | "alumni";

export const STAGES: Stage[] = ["enquiry", "registered", "active", "holidayOnly", "lapsed", "alumni"];

export const STAGE_LABEL: Record<Stage, string> = {
  enquiry: "Enquiry",
  registered: "Registered",
  active: "Active",
  holidayOnly: "Holiday only",
  lapsed: "Lapsed",
  alumni: "Alumni",
};

export type Service = "preschool" | "holiday";

export interface CrmNote {
  id: string;
  /** ISO timestamp */
  at: string;
  text: string;
}

/** An enquiry the manager typed in (no form or invoice yet). */
export interface CrmEnquiry {
  parentName: string;
  email: string;
  phone: string;
  address: string;
  childName: string;
  childDateOfBirth: string | null;
  interest: Service | "both";
  /** ISO date of the enquiry */
  date: string;
}

/**
 * customers/{id}: a family as the manager confirmed it. Membership is a list of identity keys (see Identity.key).
 * Families nobody has touched are not stored; they are derived from the data (same email = same family).
 */
export interface CustomerDoc {
  id: string;
  members: string[];
  /** ISO timestamps */
  createdAt: string;
  updatedAt: string;
  displayName?: string | null;
  stageOverride?: { stage: Stage; note: string; at: string } | null;
  notes?: CrmNote[];
  /** Child facts the sources lack (holiday forms have no date of birth), keyed by any identity key of that child. */
  childInfo?: Record<string, { dateOfBirth?: string | null }>;
  /**
   * Manual child grouping: identity key -> group label. Identities with a label are only ever grouped with the same
   * label (never by name), so "not the same child" and "same child" both stick.
   */
  childGroups?: Record<string, string>;
  enquiry?: CrmEnquiry | null;
}

/** crmRejectedPairs/{a__b}: two identities the manager said are different families. */
export interface RejectedPair {
  id: string;
  a: string;
  b: string;
  at: string;
}

export interface CrmInput {
  children: CrmChildDoc[];
  registrations: CrmRegistrationDoc[];
  holidayTerms: CrmHolidayTerm[];
  holidayRegistrations: CrmHolidayRegistration[];
  holidayInvoices: CrmHolidayInvoice[];
  preschoolInvoices: CrmPreschoolInvoice[];
  customers: CustomerDoc[];
  rejectedPairs: RejectedPair[];
}

// ---------------------------------------------------------------------------------------------------------------
// Identities: one per source record (a preschool child, a registration, a holiday booking, an enquiry, ...).

export type IdentityKind = "preschoolChild" | "preschoolRegistration" | "holidayBooking" | "invoiceOnly" | "enquiry";

export interface Identity {
  /**
   * pre:{childId} | reg:{registrationId} | hol:{holidayRegistrationId} | man:{customerId} |
   * pinv:{childId or email/child} | hinv:{email/child}
   */
  key: string;
  kind: IdentityKind;
  service: Service | null;
  /** Short description, e.g. "Holiday Club · October 2026". */
  label: string;
  parentNames: string[];
  emails: string[];
  phones: string[];
  address: string;
  childName: string;
  dateOfBirth: string | null;
  /** ISO date the record was created (form submitted, registration, first invoice). */
  at: string | null;
  preschool?: {
    childId: string | null;
    registrationId: string | null;
    enrolment: string;
    funding: string;
    startDate: string | null;
    /** children.ignore, or only known from old invoices */
    left: boolean;
  };
  holiday?: {
    registrationId: string | null;
    termId: string;
    termName: string;
    fullDays: number;
    halfDays: number;
    firstDay: string | null;
    lastDay: string | null;
    cancelled: boolean;
    invoiceNo: number | null;
  };
  enquiry?: CrmEnquiry;
}

// ---------------------------------------------------------------------------------------------------------------
// Outputs.

export interface Money {
  invoicedPence: number;
  paidPence: number;
}

export interface LtvLine {
  label: string;
  pence: number;
  detail: string;
}

export interface Prediction {
  preschoolPence: number;
  holidayPence: number;
  lines: LtvLine[];
}

export type PreschoolStatus = "active" | "registered" | "left" | "noShow" | "none";

export interface CrmChild {
  /** The group key: the manual label, or the smallest identity key. */
  key: string;
  name: string;
  dateOfBirth: string | null;
  dobSource: "registration" | "manual" | "enquiry" | null;
  identityKeys: string[];
  services: Service[];
  preschoolStatus: PreschoolStatus;
  holidayBookings: number;
  firstHoliday: string | null;
  lastHoliday: string | null;
  /** 1 September of the reception year (UK cut-off), when the date of birth is known. */
  schoolStart: string | null;
  /** Last day of Holiday Club eligibility (31 August after Year 6), when the date of birth is known. */
  holidayUntil: string | null;
  stage: Stage;
  stageReason: string;
  preschool: Money;
  holiday: Money;
  /** Weekly preschool fee used for the forecast, and where it came from. */
  weekly: { pence: number; basis: "invoice" | "enrolment" | "average"; detail: string } | null;
  prediction: Prediction;
}

export type TimelineKind =
  "enquiry" | "registration" | "start" | "booking" | "cancelledBooking" | "invoice" | "payment" | "note" | "stage" |
  "school" | "left";

export interface TimelineEvent {
  /** ISO date (YYYY-MM-DD) or timestamp */
  at: string;
  kind: TimelineKind;
  title: string;
  detail: string;
  service: Service | null;
  amountPence: number | null;
  future: boolean;
}

export interface CrmCustomer {
  id: string;
  /** The stored document, if the manager has touched this family. */
  doc: CustomerDoc | null;
  /** Other stored documents that ended up in the same family (consolidated on the next save). */
  extraDocIds: string[];
  name: string;
  emails: string[];
  phones: string[];
  addresses: string[];
  identities: Identity[];
  children: CrmChild[];
  services: Service[];
  stage: Stage;
  derivedStage: Stage;
  stageReason: string;
  manualStage: boolean;
  firstSeen: string | null;
  lastActivity: string | null;
  timeline: TimelineEvent[];
  notes: CrmNote[];
  realised: Money & { preschoolPence: number; holidayPence: number };
  prediction: Prediction;
  /** realised (invoiced, not cancelled) + predicted future */
  ltvPence: number;
}

export type Confidence = "high" | "medium" | "low";

export interface MatchReason {
  label: string;
  weight: number;
}

export interface MergeSuggestion {
  id: string;
  a: string;
  b: string;
  score: number;
  confidence: Confidence;
  reasons: MatchReason[];
  /** Identity keys stored when the manager says "not the same". */
  pair: [string, string];
}
